import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, History, Car, DollarSign, Star, Gift, Trophy, MessageSquare, Mail, Undo2, Users, Coins } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const META = {
  booking: { icon: Car, cls: "text-[#F2B705]", label: "Rentals" },
  return: { icon: Undo2, cls: "text-white/60", label: "Returns" },
  payment: { icon: DollarSign, cls: "text-emerald-400", label: "Payments" },
  review: { icon: Star, cls: "text-amber-400", label: "Reviews" },
  referral: { icon: Users, cls: "text-purple-400", label: "Referrals" },
  club: { icon: Trophy, cls: "text-[#F2B705]", label: "Club" },
  reward: { icon: Gift, cls: "text-emerald-400", label: "Rewards" },
  points: { icon: Coins, cls: "text-sky-400", label: "Points" },
  credit: { icon: Gift, cls: "text-[#F2B705]", label: "Credits" },
  sms: { icon: MessageSquare, cls: "text-sky-400", label: "SMS" },
  email: { icon: Mail, cls: "text-white/60", label: "Emails" },
};

export default function CustomerTimeline({ token, email }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/customers/${encodeURIComponent(email)}/timeline`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [token, email]);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="animate-spin text-[#F2B705]" size={16} /></div>;
  if (!data) return null;

  const events = filter === "all" ? data.events : data.events.filter((e) => e.type === filter);

  return (
    <div className="rounded-lg bg-[#0C0C0C] border border-white/10 p-4" data-testid="customer-timeline">
      <h4 className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3">
        <History size={15} className="text-[#F2B705]" /> Customer Timeline
        <span className="text-white/35 font-normal">({data.events.length} events)</span>
        {data.is_club_member && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#F2B705]/15 text-[#F2B705] font-bold">Club Member</span>}
      </h4>

      <div className="flex gap-1.5 flex-wrap mb-3" data-testid="timeline-filters">
        <button onClick={() => setFilter("all")} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${filter === "all" ? "bg-[#F2B705] text-black border-[#F2B705]" : "border-white/15 text-white/50"}`}>All</button>
        {Object.entries(data.counts).map(([t, c]) => (
          <button key={t} data-testid={`timeline-filter-${t}`} onClick={() => setFilter(t)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${filter === t ? "bg-[#F2B705] text-black border-[#F2B705]" : "border-white/15 text-white/50"}`}>
            {META[t]?.label || t} {c}
          </button>
        ))}
      </div>

      {events.length === 0 && <p className="text-white/35 text-sm">No history yet for this customer.</p>}
      <div className="relative max-h-96 overflow-y-auto pr-1 space-y-0" data-testid="timeline-events">
        {events.map((e, i) => {
          const M = META[e.type] || META.booking;
          const Icon = M.icon;
          return (
            <div key={i} className="flex gap-3 relative pb-3">
              {i < events.length - 1 && <span className="absolute left-[11px] top-6 bottom-0 w-px bg-white/10" />}
              <span className={`h-6 w-6 rounded-full bg-black border border-white/15 flex items-center justify-center shrink-0 ${M.cls}`}><Icon size={12} /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-white/85 text-sm truncate">{e.title}{e.amount != null && e.amount !== 0 && <span className={`ml-2 font-bold ${e.type === "payment" ? "text-emerald-400" : "text-white/60"}`}>${Number(e.amount).toLocaleString()}</span>}</span>
                  <span className="text-white/30 text-[11px] whitespace-nowrap">{fmtDateTime(e.ts)}</span>
                </div>
                {e.detail && <div className="text-white/40 text-xs mt-0.5 truncate">{e.detail}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
